import type { HoleDocument } from '../course/CourseDocument';
export type EditorTool =
  | 'raise'
  | 'lower'
  | 'smooth'
  | 'flatten'
  | 'slope'
  | 'greenContour'
  | 'bunkerRecess'
  | 'measure'
  | 'validate'
  | 'vegetationPaint'
  | 'select'
  | 'editShape'
  | 'freehand'
  | 'polygon'
  | 'showColliders'
  | 'tee'
  | 'pin'
  | 'fairway'
  | 'green'
  | 'rough'
  | 'bunker'
  | 'water'
  | 'tree'
  | 'rock'
  | 'delete';
export interface CourseEditorHudActions {
  setTool(tool: EditorTool): void;
  newCourse(): void;
  addHole(): void;
  previousHole(): void;
  nextHole(): void;
  save(): void;
  load(): void;
  exportJson(): void;
  importJson(file: File): void;
  playTest(): void;
  updateHole(patch: Partial<Pick<HoleDocument, 'number' | 'par' | 'yardage' | 'name'>>): void;
  updateSelected(patch: Record<string, number | string>): void;
  undo(): void;
  redo(): void;
  finishDrawing(): void;
  returnHome?(): void;
}

export interface SelectedObjectSummary {
  id: string;
  label: string;
  x?: number;
  y?: number;
  z?: number;
  radius?: number;
  name?: string;
  surface?: string;
}

export class CourseEditorHud {
  readonly element = document.createElement('div');
  private readonly props = document.createElement('div');
  private readonly propsPanel = document.createElement('div');
  private propsOpen = false;
  private cameraHintOpen = localStorage.getItem('course-editor-camera-hint') !== 'hidden';
  private activeTool: EditorTool = 'select';
  constructor(
    container: HTMLElement,
    private readonly actions: CourseEditorHudActions,
  ) {
    this.element.className = 'course-editor-hud';
    this.element.innerHTML = `<style>.course-editor-hud{position:absolute;inset:0;pointer-events:none;font:13px system-ui;color:#eef}.ce-top,.ce-left,.ce-right{position:absolute;pointer-events:auto;background:rgba(8,18,28,.86);border:1px solid rgba(255,255,255,.18);border-radius:10px;padding:8px;display:flex;gap:6px}.ce-top{top:10px;left:50%;transform:translateX(-50%);flex-wrap:wrap}.ce-left{top:64px;left:10px;flex-direction:column}.ce-right{top:64px;right:10px;flex-direction:column;width:min(280px,calc(100vw - 24px))}.ce-right.closed{background:transparent;border:0;padding:0;width:auto}.course-editor-hud button{background:#17334d;color:#eef;border:1px solid #4d789c;border-radius:6px;padding:5px 8px}.course-editor-hud button.active{background:#2f855a}.course-editor-hud input{width:100%;box-sizing:border-box;background:#06131f;color:#fff;border:1px solid #4d789c;border-radius:4px;padding:4px}.ce-field{display:block;margin:6px 0}.ce-field span{display:block;margin-bottom:2px;color:#cfe2ff}.ce-error{color:#ffb4b4;white-space:pre-wrap}.ce-help,.ce-status,.ce-mobile{position:absolute;left:10px;right:10px;background:rgba(8,18,28,.84);padding:8px;border-radius:8px}.ce-help{bottom:10px;pointer-events:auto}.ce-status{bottom:58px;pointer-events:none;border:1px solid rgba(255,255,255,.16)}.ce-mobile{display:none;top:112px;pointer-events:none;color:#ffe8a3}.ce-dismiss{float:right;padding:1px 6px!important}.ce-props-toggle{white-space:nowrap}.ce-right.closed .ce-props-body{display:none}@media (max-width:700px){.ce-top{left:50%;max-width:48vw}.ce-left{top:58px}.ce-right{top:58px;right:8px}.ce-help,.ce-status,.ce-mobile{font-size:12px}.ce-mobile{display:block}}</style><div class="ce-top"></div><div class="ce-left"></div><div class="ce-right closed"></div><div class="ce-status"></div><div class="ce-mobile">Desktop recommended for full editing. Basic selection, moving, properties, and Play Test remain available.</div><div class="ce-help"><button class="ce-dismiss">×</button><b>Camera</b>: right-drag orbit, middle/shift-drag pan, wheel/pinch zoom. Use Select before navigating to avoid editing.</div>`;
    container.appendChild(this.element);
    this.build();
    this.element.querySelector('.ce-dismiss')?.addEventListener('click', () => {
      this.cameraHintOpen = false;
      localStorage.setItem('course-editor-camera-hint', 'hidden');
      (this.element.querySelector('.ce-help') as HTMLElement | null)?.style.setProperty(
        'display',
        'none',
      );
    });
    if (!this.cameraHintOpen)
      (this.element.querySelector('.ce-help') as HTMLElement | null)?.style.setProperty(
        'display',
        'none',
      );
  }
  private build(): void {
    const top = this.element.querySelector('.ce-top') as HTMLElement;
    const left = this.element.querySelector('.ce-left') as HTMLElement;
    const right = this.element.querySelector('.ce-right') as HTMLElement;
    const addButton = (parent: HTMLElement, label: string, onClick: () => void) => {
      const b = document.createElement('button');
      b.textContent = label;
      b.onclick = onClick;
      parent.appendChild(b);
      return b;
    };
    [
      'New course',
      'Add hole',
      'Previous hole',
      'Next hole',
      'Save',
      'Load',
      'Export JSON',
      'Play-test',
    ].forEach((label) =>
      addButton(top, label, () =>
        (this.actions as unknown as Record<string, () => void>)[
          label.toLowerCase().replace(/ |-/g, label === 'Export JSON' ? 'Json' : '')
        ]?.(),
      ),
    );
    top.innerHTML = '';
    addButton(top, 'New course', this.actions.newCourse);
    addButton(top, 'Add hole', this.actions.addHole);
    addButton(top, 'Previous hole', this.actions.previousHole);
    addButton(top, 'Next hole', this.actions.nextHole);
    addButton(top, 'Save', this.actions.save);
    addButton(top, 'Load', this.actions.load);
    addButton(top, 'Export JSON', this.actions.exportJson);
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = () => {
      const f = input.files?.[0];
      if (f) this.actions.importJson(f);
    };
    top.appendChild(input);
    addButton(top, 'Play-test', this.actions.playTest);
    addButton(top, 'Undo', this.actions.undo);
    addButton(top, 'Redo', this.actions.redo);
    addButton(top, 'Finish Draw', this.actions.finishDrawing);
    if (this.actions.returnHome) addButton(top, 'Return Home', this.actions.returnHome);
    const tools: EditorTool[] = [
      'raise',
      'lower',
      'smooth',
      'flatten',
      'slope',
      'greenContour',
      'bunkerRecess',
      'select',
      'editShape',
      'measure',
      'validate',
      'tee',
      'pin',
      'freehand',
      'polygon',
      'fairway',
      'green',
      'rough',
      'bunker',
      'water',
      'tree',
      'rock',
      'vegetationPaint',
      'delete',
      'showColliders',
    ];
    for (const tool of tools)
      addButton(left, tool[0].toUpperCase() + tool.slice(1), () => this.setActiveTool(tool));
    const propsToggle = addButton(right, 'Properties', () => this.toggleProperties());
    propsToggle.className = 'ce-props-toggle';
    this.propsPanel.className = 'ce-props-body';
    this.propsPanel.appendChild(this.props);
    right.appendChild(this.propsPanel);
  }
  private toggleProperties(): void {
    this.propsOpen = !this.propsOpen;
    const right = this.element.querySelector('.ce-right') as HTMLElement;
    right.classList.toggle('closed', !this.propsOpen);
    const toggle = right.querySelector('.ce-props-toggle');
    if (toggle) toggle.textContent = this.propsOpen ? 'Hide Properties' : 'Properties';
  }
  setActiveTool(tool: EditorTool): void {
    this.activeTool = tool;
    this.actions.setTool(tool);
    for (const b of this.element.querySelectorAll('.ce-left button'))
      b.classList.toggle('active', b.textContent?.toLowerCase() === tool);
  }
  render(
    hole: HoleDocument | undefined,
    selected: SelectedObjectSummary | null,
    message = '',
    status = '',
  ): void {
    const selectedHtml = selected
      ? `<hr><b>${selected.label}</b>${this.field('sel-x', 'X', selected.x)}${this.field('sel-y', 'Elevation/Y', selected.y)}${this.field('sel-z', 'Z', selected.z)}${this.field('sel-radius', 'Radius / size', selected.radius)}${this.field('sel-name', 'Name / label', selected.name, 'text')}${selected.surface ? `<div class="ce-field"><span>Terrain surface</span>${selected.surface}</div>` : ''}`
      : '<hr><div>Selected: none</div>';
    const statusEl = this.element.querySelector('.ce-status') as HTMLElement | null;
    if (statusEl)
      statusEl.textContent =
        status ||
        `Tool: ${this.activeTool} · ${selected ? `Selected ${selected.label}` : 'No selection'}`;
    this.props.innerHTML = hole
      ? `<b>Hole Properties</b>${this.field('ce-num', 'Hole #', hole.number)}${this.field('ce-par', 'Par', hole.par)}${this.field('ce-yardage', 'Distance / yardage', hole.yardage)}${this.field('ce-name', 'Hole name', hole.name, 'text')}<div>Tool: ${this.activeTool}</div>${selectedHtml}<div class="ce-error">${message}</div>`
      : `<b>No hole</b><div class="ce-error">${message}</div>`;
    (this.props.querySelector('#ce-num') as HTMLInputElement | null)?.addEventListener(
      'change',
      (e) => this.actions.updateHole({ number: Number((e.target as HTMLInputElement).value) }),
    );
    (this.props.querySelector('#ce-par') as HTMLInputElement | null)?.addEventListener(
      'change',
      (e) => this.actions.updateHole({ par: Number((e.target as HTMLInputElement).value) }),
    );
    (this.props.querySelector('#ce-name') as HTMLInputElement | null)?.addEventListener(
      'change',
      (e) => this.actions.updateHole({ name: (e.target as HTMLInputElement).value }),
    );
    (this.props.querySelector('#ce-yardage') as HTMLInputElement | null)?.addEventListener(
      'change',
      (e) => this.actions.updateHole({ yardage: Number((e.target as HTMLInputElement).value) }),
    );
    this.bindSelectedNumber('sel-x', 'x');
    this.bindSelectedNumber('sel-y', 'y');
    this.bindSelectedNumber('sel-z', 'z');
    this.bindSelectedNumber('sel-radius', 'radius');
    (this.props.querySelector('#sel-name') as HTMLInputElement | null)?.addEventListener(
      'change',
      (e) => this.actions.updateSelected({ name: (e.target as HTMLInputElement).value }),
    );
  }
  private field(
    id: string,
    label: string,
    value: number | string | undefined,
    type = 'number',
  ): string {
    if (value === undefined) return '';
    const inputValue = typeof value === 'number' ? Number(value.toFixed(2)) : value;
    return `<label class="ce-field"><span>${label}</span><input id="${id}" type="${type}" value="${inputValue}"></label>`;
  }
  private bindSelectedNumber(id: string, key: string): void {
    (this.props.querySelector(`#${id}`) as HTMLInputElement | null)?.addEventListener(
      'change',
      (e) => this.actions.updateSelected({ [key]: Number((e.target as HTMLInputElement).value) }),
    );
  }
  dispose(): void {
    this.element.remove();
  }
}
